import { filter, sortBy } from 'rambda';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Flyout from '@/components/common/Flyout';
import { FilePreview } from '@/components/common/file-preview';
import useConversationForm from '@/hooks/conversations/use-conversation-form';
import useConversationStore from '@/hooks/conversations/use-conversation-store';
import useFileSizeLimit from '@/hooks/files/use-file-size-limit';
import { addToast } from '@/stores/toast';
import { readableFileSize } from '@/utils/file-size';
import { getFileType } from '@/utils/file-type';
import cn from '@/utils/merge-class-names';
import type { ConversationFormDataType } from '../conversation-form';

type ItemType<T> = T extends (infer U)[] ? U : never;
type AttachmentItemType = ItemType<ConversationFormDataType['Attachment']>;

type Props = {
  files: AttachmentItemType[];
  fileLimit: number;
  onCancel: () => void;
};

interface ExtendsFileType extends AttachmentItemType {
  uuid: string;
  order?: number;
}

export default function FileSelectorFlyout({ files, fileLimit, onCancel }: Props) {
  const [show, setShow] = useState(false);
  const [innerFiles, setInnerFiles] = useState<ExtendsFileType[]>([]);
  const { t } = useTranslation();
  const { isAdminSession } = useConversationStore();
  const {
    form: { setValue, reset },
    submit,
  } = useConversationForm();
  const { getFileSizeLimit } = useFileSizeLimit(isAdminSession);

  useEffect(() => {
    if (files.length === 0) {
      setShow(false);
    } else {
      let unpassedFilesCount = 0;
      setInnerFiles(
        files.map((file, idx) => {
          let order: number | undefined;
          if (idx < fileLimit && file.size < getFileSizeLimit(file.type.split('/')[0])) {
            order = idx - unpassedFilesCount;
          } else {
            unpassedFilesCount++;
          }
          return {
            ...file,
            order,
            uuid: `${Date.now()}_${idx}`,
          };
        }),
      );
      setShow(true);
    }
  }, [fileLimit, files]);

  const selectedFiles = useMemo(() => innerFiles.filter((file) => file.order !== undefined), [innerFiles]);

  const handleClick = (file: ExtendsFileType) => () => {
    const isSelect = file.order !== undefined;

    const maxOrder = selectedFiles.length - 1;

    if (!isSelect && selectedFiles.length >= fileLimit) return;

    if (file.size > getFileSizeLimit(file.type.split('/')[0])) {
      addToast({
        type: 'error',
        message: t('common.file-size-limit', { size: readableFileSize(getFileSizeLimit(file.type.split('/')[0])) }),
      });
      return;
    }
    if (!isSelect) {
      setInnerFiles((prev) => prev.map((f) => (f.uuid === file.uuid ? { ...f, order: maxOrder + 1 } : f)));
      return;
    }

    const selectedOrder = file.order ?? 0;
    setInnerFiles((prev) =>
      prev.map((f) =>
        f.uuid === file.uuid
          ? { ...f, order: undefined }
          : f.order && f.order > selectedOrder
            ? { ...f, order: f.order - 1 }
            : f,
      ),
    );
  };

  const handleSubmit = () => {
    const selectedFiles = sortBy<ExtendsFileType>(
      (file) => (file as ExtendsFileType).order ?? 0,
      filter((file) => file.order !== undefined, innerFiles),
    );
    setValue('Attachment', selectedFiles);
    submit();
    reset();
    setShow(false);
  };

  return (
    <Flyout
      containerClassName='desktop:justify-center'
      className='m-0 pb-safe-offset-0 desktop:max-w-screen-sm'
      shouldLockScroll={false}
      show={show}
      onClose={() => {}}>
      <div className='flex max-h-70dvh w-full flex-col gap-2 desktop:gap-4 rounded-t-xl desktop:rounded-b-xl bg-neutral-0 px-1 pt-3 pb-safe-offset-3 desktop:p-6'>
        <div className='flex w-full items-center justify-between px-5'>
          <span className='font-bold'>
            {t('upload.selected')}
            {` ${selectedFiles.length} `}
          </span>

          <button
            type='button'
            className='btn btn-ghost btn-sm'
            onClick={() => {
              setInnerFiles([]);
              setShow(false);
              onCancel();
            }}>
            {t('common.cancel')}
          </button>
        </div>

        <div className='grid flex-1 desktop:gap-2 grid-cols-3 gap-1 overflow-auto'>
          {innerFiles.map((file) => (
            <div
              key={file.uuid}
              className='relative size-full h-fit'
              onClick={handleClick(file)}>
              <FilePreview
                file={file}
                fileType={getFileType(file)}
                className='aspect-square object-cover object-center pointer-events-none'
              />

              <div
                className={cn(
                  'absolute right-2 top-2 flex size-5 items-center justify-center rounded-full border border-neutral-50',
                  file.order === undefined ? 'bg-secondary/10' : 'bg-primary text-sm text-inverse',
                  file.size > getFileSizeLimit(file.type.split('/')[0]) ? 'border-error' : '',
                )}>
                {file.order !== undefined ? file.order + 1 : ''}
              </div>
            </div>
          ))}
        </div>

        <div className={cn(selectedFiles.length ? '' : 'hidden')}>
          <button
            type='button'
            className='btn btn-primary w-full'
            onClick={handleSubmit}>
            {t('upload.send')}
          </button>
        </div>
      </div>
    </Flyout>
  );
}
